/**
 * Unauthorized Page
 * 
 * Displayed when a user tries to access a resource they don't have permission for.
 * Provides clear messaging and navigation options.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ShieldX, ArrowLeft, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES } from '../constants';
import { Trans, useTranslation } from 'react-i18next';

const Unauthorized: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation('common');

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate(ROUTES.DASHBOARD);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-haat-bg-light px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full text-center"
      >
        {/* Icon */}
        <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <ShieldX className="w-12 h-12 text-[#c00035]" />
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-haat-dark mb-3">
          {t('pages.unauthorized.title')}
        </h1>

        {/* Description */}
        <p className="text-haat-gray mb-8">
          {t('pages.unauthorized.description')}
          {user && (
            <span className="block mt-2 text-sm">
              <Trans
                i18nKey="pages.unauthorized.loggedInAs"
                ns="common"
                values={{ email: user.email }}
                components={{ strong: <strong /> }}
              />
            </span>
          )}
        </p>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={handleGoBack}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 text-haat-dark font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('pages.unauthorized.actions.goBack')}
          </button>

          <button
            onClick={handleGoHome}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-haat-red text-white font-medium rounded-lg hover:bg-haat-red/90 transition-colors"
          >
            <Home className="w-4 h-4" />
            {t('pages.unauthorized.actions.goHome')}
          </button>
        </div>

        {/* Logout option */}
        <div className="mt-8">
          <button
            onClick={logout}
            className="text-sm text-haat-gray hover:text-haat-red transition-colors"
          >
            {t('pages.unauthorized.signInDifferent')}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Unauthorized;

